import { useState, useEffect } from 'react';
import { DivisionMileStone } from '../../../../../../../api/divisionMileStones/types';
import { getAuth } from '../../../../../../../redux/auth/authSlice';
import { useSelector } from 'react-redux';
import { useUpdateMileStonesBatch } from '../../../../../../../reactQuery/division/divisionMileStoneQuery';
import { useSnackbarContext } from '../../../../../../../components/globalSnackNotify/globalSnackNotifyContext';

interface UseMilestoneModalProps {
     availableMilestones: Array<{
          mileStoneCode: string;
          mileStoneName: string;
          mileStoneOrder: number;
     }>;
     availableTasks: Array<{
          taskCode: string;
          taskName: string;
          taskOrder: number;
     }>;
     currentMilestones: DivisionMileStone[];
     divisionCode: string;
     mode: 'draft' | 'released';
     onSave: (milestones: DivisionMileStone[]) => void;
     onClose: () => void;
}

export const useMilestoneModal = ({
     availableMilestones,
     availableTasks,
     currentMilestones,
     divisionCode,
     mode,
     onSave,
     onClose,
}: UseMilestoneModalProps) => {
     const { userInfo } = useSelector(getAuth);
     const [selectedMilestone, setSelectedMilestone] = useState<string>('');
     const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
     const [milestoneName, setMilestoneName] = useState<string>('');
     const [milestoneOrder, setMilestoneOrder] = useState<number>(0);
     const [isEditing, setIsEditing] = useState<boolean>(false);
     const [editingMilestoneCode, setEditingMilestoneCode] = useState<string>('');
     const [isAddingMilestone, setIsAddingMilestone] = useState<boolean>(false);
     const [newMilestoneName, setNewMilestoneName] = useState<string>('');
     const [newTaskName, setNewTaskName] = useState<string>('');

     // Local state for milestones to handle reordering
     const [localMilestones, setLocalMilestones] = useState<DivisionMileStone[]>(currentMilestones);
     const { mutate: renameTask } = useUpdateMileStonesBatch();
     const { showSnackbar } = useSnackbarContext();

     // Update local milestones when currentMilestones prop changes
     useEffect(() => {
          setLocalMilestones(currentMilestones);
     }, [currentMilestones]);

     // Group current milestones by milestone name using local state
     const groupedCurrentMilestones = localMilestones.reduce((acc, milestone) => {
          if (!acc[milestone.mileStoneName]) {
               acc[milestone.mileStoneName] = {
                    mileStoneCode: milestone.mileStoneCode,
                    mileStoneName: milestone.mileStoneName,
                    mileStoneOrder: milestone.mileStoneOrder,
                    tasks: [],
               };
          }
          acc[milestone.mileStoneName].tasks.push(milestone);
          return acc;
     }, {} as Record<string, any>);

     const handleMilestoneSelect = (milestoneName: string) => {
          setSelectedMilestone(milestoneName);

          // First try to find in available milestones
          let milestone = availableMilestones.find(m => m.mileStoneName === milestoneName);

          // If not found in available milestones, try to find by code in current milestones
          if (!milestone) {
               const currentMilestone = currentMilestones.find(m => m.mileStoneName === milestoneName);
               if (currentMilestone) {
                    milestone = availableMilestones.find(m => m.mileStoneName === currentMilestone.mileStoneName);
               }
          }

          if (milestone) {
               setMilestoneName(milestone.mileStoneName);
               setMilestoneOrder(milestone.mileStoneOrder);

               // Get current tasks for this milestone by code
               const currentTasks = groupedCurrentMilestones[milestoneName]?.tasks || [];
               setSelectedTasks(currentTasks.map((task: DivisionMileStone) => task.taskName));
          }
     };

     const handleEditMilestone = (milestoneName: string) => {
          setIsEditing(true);
          setEditingMilestoneCode(milestoneName);

          // Find the milestone in current milestones first
          const currentMilestone = currentMilestones.find(m => m.mileStoneName === milestoneName);
          if (currentMilestone) {
               // Set the selected milestone
               setSelectedMilestone(milestoneName);
               setMilestoneName(currentMilestone.mileStoneName);
               setMilestoneOrder(currentMilestone.mileStoneOrder);

               // Get all tasks for this milestone directly from localMilestones
               const currentTasks = localMilestones.filter(m => m.mileStoneName === milestoneName);
               setSelectedTasks(currentTasks.map(task => task.taskName));
          }
     };

     const handleDeleteMilestone = (milestoneName: string) => {
          // Check if any task in this milestone has isDefault set
          const milestoneTasks = currentMilestones.filter(
               milestone => milestone.mileStoneName === milestoneName
          );

          // Only allow deletion if ALL tasks have isDefault === null
          const canDelete = milestoneTasks.every(task => task.isDefault === null);

          if (!canDelete) {
               // Don't allow deletion if any task has isDefault set to true/false
               return;
          }

          // Remove all tasks for this milestone
          const updatedMilestones = currentMilestones.filter(
               milestone => milestone.mileStoneName !== milestoneName
          );
          onSave(updatedMilestones);
     };

     const handleDeleteTask = (milestoneCode: string, taskId: string) => {
          // Find the task to check if it can be deleted
          const taskToDelete = localMilestones.find(milestone => milestone.id === taskId);

          if (!taskToDelete) {
               return;
          }

          // Only allow deletion if isDefault is null
          if (taskToDelete.isDefault !== null) {
               return;
          }

          // Find and remove the specific task
          const updatedMilestones = localMilestones.filter(
               milestone => milestone.id !== taskId
          );

          // Update local state immediately for visual feedback
          setLocalMilestones(updatedMilestones);

          // Also update the parent component
          onSave(updatedMilestones);
     };



     const handleRenameTask = (taskId: string, newName: string) => {
          const exactTask = localMilestones.find(milestone => milestone.id === taskId);

          if (!exactTask) return;

          const updatedTask = {
               ...exactTask,
               taskName: newName,
               updatedOnDate: new Date(),
          }

          renameTask(
               { milestones: [updatedTask], mode: "draft", workFlowId: taskId },
               {
                    onSuccess: () => { showSnackbar("Task renamed successfully!", "success");},
                    onError: () => { showSnackbar("Failed to rename task", "error"); },
               }
          );
     };

     const handleSave = () => {
          if (!selectedMilestone || selectedTasks.length === 0) {
               return;
          }

          const newMilestones: DivisionMileStone[] = selectedTasks.map((taskName, index) => {
               const task = availableTasks.find(t => t.taskName === taskName);
               const milestone = availableMilestones.find(m => m.mileStoneName === selectedMilestone);

               if (!task || !milestone) return null;

               return new DivisionMileStone(
                    `${task.taskCode}_${milestone.mileStoneCode}_${divisionCode}_${mode}_${Date.now()}`,
                    false, // automated
                    null, // isDefault - null means it can be deleted
                    divisionCode,
                    milestone.mileStoneCode,
                    milestoneName,
                    milestoneOrder,
                    mode,
                    task.taskCode,
                    task.taskName,
                    task.taskOrder,
                    false, // status
                    new Date(),
                    userInfo?.username,
                    undefined,
                    undefined
               );
          }).filter(Boolean) as DivisionMileStone[];

          // If editing, remove existing tasks for this milestone
          let updatedMilestones = currentMilestones;
          if (isEditing) {
               updatedMilestones = currentMilestones.filter(
                    milestone => milestone.mileStoneName !== editingMilestoneCode
               );
          }

          // Add new milestones
          const finalMilestones = [...updatedMilestones, ...newMilestones];
          onSave(finalMilestones);

          resetForm();
          onClose();
     };

     const handleSaveNewMilestone = () => {
          if (!newMilestoneName.trim() || !newTaskName.trim()) {
               return;
          }

          // Check if we're adding a task to an existing milestone or creating a new milestone
          const existingMilestone = localMilestones.find(m => m.mileStoneName === newMilestoneName);

          if (existingMilestone) {
               // Adding a task to an existing milestone
               const newTaskCode = `TASK_${Date.now()}`;
               const newTask = new DivisionMileStone(
                    "",
                    false,
                    null,
                    divisionCode,
                    existingMilestone.mileStoneCode,
                    newMilestoneName.trim(),
                    existingMilestone.mileStoneOrder,
                    mode,
                    newTaskCode,
                    newTaskName.trim(),
                    999, // high order number for new tasks
                    false, // status
                    new Date(),
                    userInfo?.username,
                    new Date(),
                    undefined
               );

               // Only send the new task to the API, not all existing milestones
               onSave([newTask]);
          } else {
               // Generate a code using the first 3 uppercase letters of the milestone name (acronym style), padded if needed
               const acro = newMilestoneName
                    .replace(/[^a-zA-Z]/g, '')
                    .toUpperCase()
                    .slice(0, 3)
                    .padEnd(3, 'X')
                    .concat(`_${Date.now()}`); /* Returns the number of milliseconds elapsed since midnight, January 1, 1970 Universal Coordinated Time (UTC) */

               const newMilestoneCode = acro;
               const newMilestone = new DivisionMileStone(
                    "",
                    false, // automated
                    null, // isDefault - null means it can be deleted
                    divisionCode,
                    newMilestoneCode,
                    newMilestoneName.trim(),
                    999, // high order number for new milestones
                    mode,
                    `TASK_${Date.now()}`,
                    newTaskName.trim(),
                    1,
                    false, // status
                    new Date(),
                    userInfo?.username || 'current-user',
                    new Date(),
                    undefined
               );

               // Only send the new milestone to the API, not all existing milestones
               onSave([newMilestone]);
          }

          // Reset form
          setNewMilestoneName('');
          setNewTaskName('');
          setIsAddingMilestone(false);
     };



     const resetForm = () => {
          setSelectedMilestone('');
          setSelectedTasks([]);
          setMilestoneName('');
          setMilestoneOrder(0);
          setIsEditing(false);
          setEditingMilestoneCode('');
          setIsAddingMilestone(false);
          setNewMilestoneName('');
          setNewTaskName('');
     };

     const handleClose = () => {
          resetForm();
          onClose();
     };

     const handleReorderMilestones = (sourceIndex: number, destinationIndex: number) => {
          // Group milestones by milestone name
          const sortedMilestones = Object.values(groupedCurrentMilestones)
               .sort((a, b) => a.mileStoneOrder - b.mileStoneOrder);

          // Perform the reorder
          const [removed] = sortedMilestones.splice(sourceIndex, 1);
          sortedMilestones.splice(destinationIndex, 0, removed);

          // Update orders and create new milestone array
          const updatedMilestones = localMilestones.map(milestone => {
               const newOrderIndex = sortedMilestones.findIndex(m => m.mileStoneCode === milestone.mileStoneCode);
               if (newOrderIndex !== -1) {
                    return {
                         ...milestone,
                         mileStoneOrder: newOrderIndex + 1, // 1-based ordering
                         updatedOnDate: new Date(),
                    };
               }
               return milestone;
          });

          // Update local state immediately for visual feedback
          setLocalMilestones(updatedMilestones);

          // Save through the parent's onSave callback which handles API calls
          onSave(updatedMilestones);
     };

     return {
          // State
          selectedMilestone,
          selectedTasks,
          milestoneName,
          milestoneOrder,
          isEditing,
          editingMilestoneCode,
          isAddingMilestone,
          newMilestoneName,
          newTaskName,
          localMilestones,

          // Setters
          setSelectedTasks,
          setIsAddingMilestone,
          setNewMilestoneName,
          setNewTaskName,
          setIsEditing,
          setEditingMilestoneCode,

          // Handlers
          handleMilestoneSelect,
          handleEditMilestone,
          handleDeleteMilestone,
          handleDeleteTask,
          handleRenameTask,
          handleSave,
          handleSaveNewMilestone,
          handleClose,
          handleReorderMilestones,
          resetForm,
     };
};